import { useForm } from 'react-hook-form'

import { useEffect } from 'react'

import { cn } from '@/lib/utils'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'

import { TextInput } from '@/components/atoms'
import { useEnterSubmit } from '@/hooks/ui'

import { RoleModel } from '@/types'

import { PERMISSIONS } from '../../../../permissions'
import { RoleFormState, roleFieldsConfig } from './roleFieldsConfig'
import { Spinner } from '@/components/ui/spinner'

// Root permissions.ts

interface RoleDialogProps {
  open: boolean
  mode: 'create' | 'edit' | 'view'
  editRole: RoleModel.Role | null
  closeDialog: () => void
  onCreate: (data: RoleFormState) => void
  onUpdate: (id: string, data: RoleFormState) => void
  loading?: boolean
}

export const RoleDialog = ({
  open,
  mode,
  editRole,
  closeDialog,
  onCreate,
  onUpdate,
  loading = false,
}: RoleDialogProps) => {
  const {
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    control,
  } = useForm<RoleFormState>({
    defaultValues: {
      name: '',
      permissions: [],
    },
  })

  const formValues = watch()
  const { fields } = roleFieldsConfig({ formValues })

  const selectedPermissions = formValues.permissions || []

  useEffect(() => {
    if (open) {
      if ((mode === 'edit' || mode === 'view') && editRole) {
        reset({
          name: editRole.name,
          permissions: editRole.permissions || [],
        })
      } else {
        reset({
          name: '',
          permissions: [],
        })
      }
    }
  }, [open, mode, editRole, reset])

  const onSubmit = (data: RoleFormState) => {
    if (mode === 'create') {
      onCreate(data)
    } else {
      if (!editRole?._id) return
      onUpdate(editRole._id, data)
    }
  }

  useEnterSubmit(handleSubmit(onSubmit), open && mode !== 'view')

  const togglePermission = (permission: string) => {
    if (mode === 'view') return
    const current = [...selectedPermissions]
    const index = current.indexOf(permission)
    if (index > -1) {
      current.splice(index, 1)
    } else {
      current.push(permission)
    }
    setValue('permissions', current, { shouldValidate: true })
  }

  const toggleModule = (module: string, actions: string[]) => {
    if (mode === 'view') return
    const modulePermissions = actions.map((a) => `${module}:${a}`)
    const allSelected = modulePermissions.every((p) => selectedPermissions.includes(p))

    let nextPermissions = [...selectedPermissions]
    if (allSelected) {
      // Unselect all
      nextPermissions = nextPermissions.filter((p) => !modulePermissions.includes(p))
    } else {
      // Select all
      modulePermissions.forEach((p) => {
        if (!nextPermissions.includes(p)) {
          nextPermissions.push(p)
        }
      })
    }
    setValue('permissions', nextPermissions, { shouldValidate: true })
  }

  const isModuleSelected = (module: string, actions: string[]) => {
    const modulePermissions = actions.map((a) => `${module}:${a}`)
    return modulePermissions.every((p) => selectedPermissions.includes(p))
  }

  const isModulePartial = (module: string, actions: string[]) => {
    const modulePermissions = actions.map((a) => `${module}:${a}`)
    const selectedCount = modulePermissions.filter((p) => selectedPermissions.includes(p)).length
    return selectedCount > 0 && selectedCount < modulePermissions.length
  }

  return (
    <Dialog open={open} onOpenChange={closeDialog}>
      <DialogContent size="lg" className="flex h-[85vh] flex-col gap-0 overflow-hidden p-0 sm:h-[90vh]">
        <div className="flex h-full flex-col">
          <DialogHeader className="border-b border-border pb-4">
            <DialogTitle>
              {mode === 'create'
                ? 'Create role'
                : mode === 'edit'
                  ? `Edit ${editRole?.name}`
                  : 'Role details'}
            </DialogTitle>
            <DialogDescription>
              {mode === 'view'
                ? 'Permissions granted to this role.'
                : 'Name the role and choose what staff with this role can do.'}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1">
            <div className="space-y-6 p-6">
              <TextInput
                control={control}
                name="name"
                label="Role name"
                placeholder="e.g. Sales Manager"
                disabled={mode === 'view'}
                rules={fields.name.rules}
                errors={errors}
              />

              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-semibold text-ink">Permissions</Label>
                    <p className="text-sm text-text-2">Select a section to grant all of its permissions.</p>
                  </div>
                  <Badge tone="soft" className="tabular-nums">
                    {selectedPermissions.length} selected
                  </Badge>
                </div>

                {errors.permissions && (
                  <p className="text-sm text-error">
                    {errors.permissions.message}
                  </p>
                )}

                <div className="space-y-3">
                  {PERMISSIONS.map((group) => {
                    const allSelected = isModuleSelected(group.module, group.actions)
                    const partial = isModulePartial(group.module, group.actions)

                    return (
                      <div key={group.module} className="rounded-lg border border-border">
                        <div className="flex items-center justify-between gap-3 border-b border-border-subtle px-4 py-3">
                          <div className="flex items-center gap-3">
                            <Checkbox
                              id={`module-${group.module}`}
                              checked={allSelected ? true : partial ? 'indeterminate' : false}
                              onCheckedChange={() => toggleModule(group.module, group.actions)}
                              disabled={mode === 'view'}
                            />
                            <Label
                              htmlFor={`module-${group.module}`}
                              className="cursor-pointer text-sm font-semibold capitalize text-ink"
                            >
                              {group.module.replace(/_/g, ' ')}
                            </Label>
                          </div>
                          <p className="text-xs tabular-nums text-text-2">
                            {group.actions.length} {group.actions.length === 1 ? 'permission' : 'permissions'}
                          </p>
                        </div>

                        <div className="grid grid-cols-1 gap-x-4 gap-y-1 p-3 sm:grid-cols-2 md:grid-cols-3">
                          {group.actions.map((action) => {
                            const permissionKey = `${group.module}:${action}`
                            const isSelected = selectedPermissions.includes(permissionKey)

                            return (
                              <div
                                key={permissionKey}
                                className={cn(
                                  'flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-subdued',
                                  mode === 'view' && 'cursor-default hover:bg-transparent'
                                )}
                                onClick={() => togglePermission(permissionKey)}
                              >
                                <Checkbox
                                  checked={isSelected}
                                  disabled={mode === 'view'}
                                  tabIndex={-1}
                                  aria-hidden="true"
                                  className="pointer-events-none"
                                />
                                <span className="text-sm capitalize text-ink">
                                  {action.replace(/_/g, ' ')}
                                </span>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeDialog}
              disabled={loading}>
              {mode === 'view' ? 'Close' : 'Cancel'}
            </Button>
            {mode !== 'view' && (
              <Button
                type="button"
                disabled={loading}
                onClick={handleSubmit(onSubmit)}>
                {loading && <Spinner />}
                {mode === 'create' ? 'Create role' : 'Save'}
              </Button>
            )}
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
